import EmptyState from '../util/EmptyState';
import UserAvatar from '../util/UserAvatar';
import React, { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableList, faStopwatch, faUserGroup } from '@fortawesome/free-solid-svg-icons';
import Paginations from '../util/Paginations';
import TableSortLabel from '@mui/material/TableSortLabel';
import { t } from 'i18next';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: 'var(--text-color)',
  padding: '8px 10px',
  backgroundColor: 'var(--background-color)',
  borderBottom: 'var(--border-input)'
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
  color: 'var(--text-color)',
  padding: '7px 15px',
  backgroundColor: 'var(--btn-user-form)',
  borderBottom: 'var(--border-input)',
  fontSize: 13
}));

interface ReportsTableProps {
  users: any;
  handleShow: any;
  handleShowToils: any;
  selectedContractFilter: any;
  access: any;
  baseUrl: string;
}

const UsersToils: React.FC<ReportsTableProps> = ({
  users, handleShow, handleShowToils, selectedContractFilter, access, baseUrl }) => {
  const [page, setPage] = React.useState(0);
  const [orderBy, setOrderBy] = React.useState('first_name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  const convertToHoursAndMinutes = (totalToils: number) => {
    const isNegative = totalToils < 0;
    const absoluteTotalToils = Math.abs(totalToils);
    const hours = Math.trunc(absoluteTotalToils);
    const minutes = Math.round((absoluteTotalToils - hours) * 60);
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${isNegative ? '-' : ''}${hours}:${formattedMinutes}`;
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
  }, [users])

  return (
    <>
      {users.length ?
        (<div className='mb-[10px] text-center'>
          <TableContainer
            component={Paper}
            style={{ boxShadow: 'none', backgroundColor: 'inherit', borderBottom: 'var(--border-input)' }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    <TableSortLabel
                      active={orderBy === 'first_name'}
                      direction={orderBy === 'first_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('first_name')}
                      sx={{ pl: '10px' }}>
                      {t('Name')}
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell>{t('HrWorkingPattern')}</StyledTableCell>
                  <StyledTableCell>{t('ContractedHoursPerWeek')}</StyledTableCell>
                  <StyledTableCell>{t('TotalTOIL')}</StyledTableCell>
                  <StyledTableCell align="right" width='8%' sx={{ pr: '15px' }}>{t('Edit')}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                  ?.filter((user: any) => selectedContractFilter === null || user.patternId === selectedContractFilter)
                  .sort((a: any, b: any) => {
                    const compareValueA = order === 'asc' ? a.first_name : b.first_name;
                    const compareValueB = order === 'asc' ? b.first_name : a.first_name;
                    return compareValueA.localeCompare(compareValueB);
                  })
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user: any, index: any) => (
                    <TableRow
                      key={user.dateId || index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <StyledTableRow
                        component="th"
                        scope="row">
                        {<div className="flex gap-[10px] items-center cursor-pointer"
                          onClick={() => handleShow(user)}>
                          <UserAvatar user={user} baseUrl={baseUrl} size={30} />
                          <div className='flex gap-[5px] item-center flex-wrap'>
                            <div className="user-box-name">{user.first_name}</div>
                            <div className="user-box last">{user.last_name}</div>
                            <div className="user-box second">{user.second_name}</div>
                          </div>
                        </div>}
                      </StyledTableRow>
                      <StyledTableRow>{user.pattern?.name}</StyledTableRow>
                      <StyledTableRow>{convertToHoursAndMinutes(user.hoursPerWeek ?? 0)}</StyledTableRow>
                      <StyledTableRow>{convertToHoursAndMinutes(user.totalToils)}</StyledTableRow>
                      <StyledTableRow align="right" sx={{ pr: '15px' }}>
                        <div className='flex gap-[7px] justify-end items-center'>
                          {access.toil_edit_all ? (
                            <FontAwesomeIcon
                              icon={faStopwatch}
                              className='text-[16px] cursor-pointer opacity-50 text-[var(--text-color)] hover:opacity-70'
                              onClick={() => handleShowToils(user)} />) : ''}
                          <FontAwesomeIcon
                            icon={faTableList}
                            className='text-[16px] cursor-pointer opacity-50 text-[var(--text-color)] hover:opacity-70'
                            onClick={() => handleShow(user)} /></div>
                      </StyledTableRow>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div className='absolute bottom-0 left-0 p-[5px] flex justify-between bg-[var(--bg-top-fix)] w-full items-center rounded-b-[5px] pr-[20px]'>
            <Paginations
              rows={users}
              rowsPerPage={rowsPerPage}
              page={page}
              setRowsPerPage={setRowsPerPage}
              setPage={setPage}
              arrayPages={[10, 20, 50]}
            />
            <div className="flex gap-[5px] items-center text-[16px]">
              <div className="user-count-view flex gap-[5px]"><span className='user-count opacity-70'>{users?.length}</span></div>
              <FontAwesomeIcon icon={faUserGroup} className='text-[16px] opacity-30 text-[var(--text-color)]' />
            </div>
          </div>
        </div>
        ) : (
          <div className='flex items-center justify-center'>
            <EmptyState message={t('No users available') as string} />
          </div>
        )}
    </>
  );
};

export default UsersToils;